<template>
  <div class="chat-page-container">
    <div class="chat-body-container">
      <section class="chat-body-conversation" ref="conversationContainer">
        <template v-for="(item, index) in groupedConversation" :key="index">
          <div v-if="item.type === 'separator'" class="date-separator">
            <v-divider>
              <span class="separator-text">{{ item.text }}</span>
            </v-divider>
          </div>

          <div v-else :class="['message', item.isUser ? 'user' : 'assistent']">
            <div class="message-wrapper">
              <v-avatar v-if="!item.isUser" size="32" :color="assistantChatStore.image ? undefined : 'primary-60'">
                <v-img v-if="assistantChatStore.image" :src="assistantChatStore.image" class="avatar-image" :alt="assistantChatStore.name" />
                <span v-else class="avatar-initials">{{ getInitials(assistantChatStore.name) }}</span>
              </v-avatar>
              <div class="text-content">
                <div class="message-header">
                  <h3 class="sender-name" @mouseenter="setTooltipOnOverflow($event, item.sender)">{{ item.sender }}</h3>
                  <span class="sended-timestamp">{{ formatDateTime(item.timestamp) }}</span>
                </div>
                <div class="message-text">
                  <template v-if="item.isUser">{{ item.text }}</template>
                  <template v-else>
                    <v-chip v-if="item.code" label class="message-code mr-2" color="primary-35" size="x-small">
                      {{ item.code }}
                    </v-chip>
                    <span v-html="formatMessage(item.text)"></span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!item.isUser && item.lgpdButtonConfig && (item.lgpdButtonConfig.confirmKeyword || item.lgpdButtonConfig.rejectKeyword)" class="lgpd-action-buttons">
            <v-btn
              v-if="item.lgpdButtonConfig.rejectKeyword"
              variant="outlined"
              color="primary-35"
              rounded="xl"
              class="lgpd-btn reject"
              @click="!(item.lgpdButtonConfig.selected || isFetchingResponse) ? sendLgpdAction(item.lgpdButtonConfig.rejectKeyword, item.lgpdButtonConfig) : ''"
            >
              {{ item.lgpdButtonConfig.rejectKeyword }}
            </v-btn>
            <v-btn
              v-if="item.lgpdButtonConfig.confirmKeyword"
              variant="flat"
              color="primary-35"
              rounded="xl"
              class="lgpd-btn accept"
              @click="!(item.lgpdButtonConfig.selected || isFetchingResponse) ? sendLgpdAction(item.lgpdButtonConfig.confirmKeyword, item.lgpdButtonConfig) : ''"
            >
              {{ item.lgpdButtonConfig.confirmKeyword }}
            </v-btn>
          </div>
        </template>
      </section>

      <section class="chat-body-text">
        <div v-if="isFetchingResponse" class="assistent-typing ">
          <div class="typing">
            <span />
            <span />
            <span />
          </div>
          <p>{{ assistantChatStore.name }} está escrevendo...</p>
        </div>

        <div class="input-wrapper">
          <rb-textarea v-model="userMessage" autofocus color="neutral-35" @keydown.enter="handleEnterKey" @focus="isFocused = true" @blur="isFocused = false" placeholder="Escreva aqui sua mensagem" :rows="rows" max-rows="10" no-resize class="flex-grow-1" variant="outlined" hide-details ref="messageInput" auto-grow />
          <v-btn @click="isFetchingResponse ? stopFetching() : send()" icon color="primary-60" flat>
            <v-icon v-if="isFetchingResponse">mdi-stop</v-icon>
            <img v-else :src="require('@/ai_studio/assets/icons/send-icon.svg')" alt="Enviar">
          </v-btn>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, nextTick, computed } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import { formatDateLabel, formatBold, formatDateTime, formatUrls, getInitials } from "@/ai_studio/utils/textFormatter";
import { useAssistantChatStore } from "@/assistents/stores/assistantChat";
import AppException from "@/assistents/exceptions/AppException";

const DEFAULT_ERROR_MESSAGE = "Não foi possível processar a solicitação, tente novamente mais tarde.";
const DEFAULT_ERROR_CODE = "000";

const route = useRoute();
const assistantChatStore = useAssistantChatStore();
const isFetchingResponse = ref(false);
const userMessage = ref("");
const conversation = ref([]);
const rows = ref(1);
const abortController = ref(null);
const conversationContainer = ref(null);
const messageInput = ref(null);
const isFocused = ref(false);

const assistantKey = computed(() => route.params.key);

const userCode = computed(() => {
  const userCodeUrl = route.query.user_code;
  if (userCodeUrl) {
    return userCodeUrl;
  }
  return crypto.randomUUID();
});

const groupedConversation = computed(() => {
  if (conversation.value.length === 0) {
    return [];
  }

  const result = [];
  let lastDateLabel = null;

  conversation.value.forEach((message) => {
    const currentDateLabel = formatDateLabel(message.timestamp);
    if (lastDateLabel !== currentDateLabel) {
      lastDateLabel = currentDateLabel;
      result.push({ type: "separator", text: currentDateLabel });
    }
    result.push(message);
  });

  return result;
});


const formatMessage = (text) => {
  if (!text) return "";
  let formattedText = text;
  formattedText = formatBold(formattedText);
  formattedText = formatUrls(formattedText);
  return formattedText;
};

const setTooltipOnOverflow = (event, text) => {
  const element = event.target;
  const isOverflowing = element.scrollWidth > element.clientWidth;
  if (isOverflowing && !element.hasAttribute("title")) {
    element.setAttribute("title", text);
  }
};

const resolveError = (error) => {
  if (error instanceof AppException && error.message) {
    return { message: error.message, code: error.code };
  }
  const responseData = error.response?.data;
  if (responseData?.message) {
    return { message: responseData.message, code: responseData.error_code || DEFAULT_ERROR_CODE };
  }
  return { message: DEFAULT_ERROR_MESSAGE, code: DEFAULT_ERROR_CODE };
};

const send = async (message = null, hideUserMessage = false) => {
  if (message === null) {
    message = userMessage.value.trim();
    userMessage.value = "";
    rows.value = 1;
  }
  if (!message || isFetchingResponse.value) return;

  conversation.value = conversation.value.map(m =>
    m.lgpdButtonConfig && !m.lgpdButtonConfig.selected ? { ...m, lgpdButtonConfig: { ...m.lgpdButtonConfig, selected: true } } : m
  );
  
  if (!hideUserMessage) {
    addMessage(true, message);
  }
  
  scrollToBottom();

  isFetchingResponse.value = true;
  abortController.value?.abort();
  abortController.value = new AbortController();
  try {
    const { data } = await axios.post('/api/assistents/startBotClient', {
        msg: message,
        key: assistantKey.value,
        type: "json",
        user_code: userCode.value,
        idContato: route.query.idContato || "",
        idRegistro: route.query.idRegistro || "",
        codRegistro: route.query.codRegistro || "",
      },
      {signal: abortController.value.signal},
    );
    if (!data.success) {
      throw new AppException(data.error_code, data.message);
    }

    const { lgpd } = data;
    let lgpdButtonConfig = null;
    if (lgpd?.confirm_keyword || lgpd?.reject_keyword) {
      lgpdButtonConfig = { 
        'confirmKeyword': lgpd.confirm_keyword,
        'rejectKeyword': lgpd.reject_keyword,
        'selected': false 
      };
    }

    addMessage(false, data.dados_html || data.dados, data.assistent_data?.name, null, lgpdButtonConfig);
  } catch (error) {
    if (!axios.isCancel(error)) {
      const { message, code } = resolveError(error);
      addMessage(false, message, null, code);
    }
  } finally {
    isFetchingResponse.value = false;
    abortController.value = null;
    scrollToBottom();
  }
};

const addMessage = (isUser, message, senderName = null, code = null, lgpdButtonConfig = null) => {
  conversation.value.push({
    sender: isUser ? "Você" : senderName ?? assistantChatStore.name,
    text: message,
    isUser: isUser,
    code: code,
    timestamp: new Date(),
    lgpdButtonConfig: lgpdButtonConfig,
  });
};

const stopFetching = () => {
  abortController.value?.abort();
  isFetchingResponse.value = false;
};

const sendLgpdAction = (keyword, lgpdButtonConfig) => {
  if (isFetchingResponse.value || lgpdButtonConfig.selected) {
    return;
  }  
  
  const message = conversation.value.find(message => message.lgpdButtonConfig === lgpdButtonConfig);
  if (message) {
    message.lgpdButtonConfig.selected = true;
  }
  
  addMessage(true, keyword);
  send(keyword, true);
};

const scrollToBottom = () => {
  nextTick(() => {
    const container = conversationContainer.value;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  });
};

const handleEnterKey = (event) => {
  if (event.shiftKey) return;
  event.preventDefault();
  send();
};

const handleGlobalKeyPress = (event) => {
  const { key } = event;

  if (key === "Escape") {
    messageInput.value.blur();
    isFocused.value = false;
    return;
  }

  if (isFocused.value || key === "Enter") return;

  messageInput.value.focus();
  isFocused.value = true;
};

onMounted(() => {
  document.title = "Assistente - Chat";
  window.addEventListener("keydown", handleGlobalKeyPress);

  const initialMsg = route.query.msg;
  if (initialMsg) {
    userMessage.value = initialMsg;
    send();
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleGlobalKeyPress);
  stopFetching();
});
</script>

<style lang="scss" scoped>
$dot-width: 5px;
$dot-color: #A2A2A2;
$speed: 1.5s;

.chat-page-container {
  background: rgb(var(--v-theme-neutral-98));
  width: 100vw;
  height: 100vh;
  font-family: Roboto, sans-serif;
}

.chat-body-container {
  background: #FFFFFF;
  margin: 0 auto;
  width: 100%;
  max-width: 800px;
  height: 100vh;
  padding: 24px 32px 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 960px) {
    padding: 24px;
  }

  @media (max-width: 600px) {
    padding: 16px;
  }
}

.chat-body-conversation {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 4px;
  margin-bottom: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }
}

.chat-body-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  width: 100%;
  gap: 8px;
  flex-shrink: 0;
  min-height: 80px;

  @media (max-width: 600px) {
    min-height: 70px;
  }
}

.date-separator {
  width: 100%;
  text-align: center;
  margin: 8px 0;

  .separator-text {
    font-family: Roboto, sans-serif;
    font-size: 14px;
    color: rgb(var(--v-theme-neutral-35));
    background-color: #FFFFFF;
    padding: 0 8px;

    @media (max-width: 600px) {
      font-size: 12px;
    }
  }
}

.typing {
  position: relative;
  padding-bottom: 8px;
  width: 20px;

  span {
    content: '';
    animation: blink $speed infinite;
    animation-fill-mode: both;
    height: $dot-width;
    width: $dot-width;
    background: $dot-color;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 50%;

    &:nth-child(2) {
      animation-delay: .2s;
      margin-left: $dot-width * 1.5;
    }

    &:nth-child(3) {
      animation-delay: .4s;
      margin-left: $dot-width * 3;
    }
  }
}

.chat-textarea ::v-deep .v-field.v-field--focused .v-field__outline {
  color: #C4C7C6 !important;
}

@keyframes blink {
  0% {
    opacity: .1;
  }

  20% {
    opacity: 1;
  }

  100% {
    opacity: .1;
  }
}

.message {
  border-radius: 8px;
  font-family: Roboto, sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
  color: rgb(var(--v-theme-neutral-10));
  padding: 16px;
  max-width: 528px;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    max-width: 100%;
    padding: 12px;
    font-size: 14px;
    line-height: 20px;
  }

  .message-wrapper {
    display: flex;
    width: 100%;
    gap: 16px;

    @media (max-width: 600px) {
      gap: 12px;
    }
  }

  .text-content {
    flex: 1;
    min-width: 0;
  }

  .message-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;
    min-width: 0;
  }

  .sender-name {
    font-weight: 500;
    font-size: 16px;
    line-height: 23.4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;

    @media (max-width: 600px) {
      font-size: 14px;
      line-height: 20px;
    }
  }

  .sended-timestamp {
    color: rgb(var(--v-theme-neutral-60));
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
    flex-shrink: 0;

    @media (max-width: 600px) {
      font-size: 12px;
    }
  }
}

.message-text {
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;

  ::v-deep a {
    color: rgb(var(--v-theme-primary-60));
    text-decoration: underline;

    &:hover {
      color: rgb(var(--v-theme-primary-40));
    }
  }

  ::v-deep strong {
    font-weight: 600;
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar-initials {
  font-size: 16px;
  line-height: 24px;
}

.assistent {
  background-color: rgb(var(--v-theme-neutral-95));
  margin-right: auto;

  .sender-name {
    text-align: left;
  }
}

.user {
  margin-left: auto;
  background-color: #fff;
  border: 1px solid rgb(var(--v-theme-neutral-80));

  .message-wrapper {
    gap: 0;
  }

  .message-header {
    justify-content: flex-end;
  }

  .sender-name {
    text-align: right;
  }
}

.assistent-typing {
  display: flex;
  gap: 14px;
  align-items: center;

  @media (max-width: 600px) {
    gap: 10px;
  }

  p {
    color: rgb(var(--v-theme-neutral-60));
    font-family: Roboto, sans-serif;
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0.25px;
    margin: 0;

    @media (max-width: 600px) {
      font-size: 12px;
    }
  }
}

.lgpd-action-buttons {
  display: flex;
  gap: 8px;
  max-width: 528px;

  @media (max-width: 600px) {
    max-width: 100%;
  }

  .lgpd-btn {
    flex: 1;
    min-width: 0;
    text-transform: none;
    letter-spacing: 0;

    :deep(.v-btn__content) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }

    &.reject {
      border-color: #C5C7C6;
    }
  }
}

.input-wrapper {
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: center;

  @media (max-width: 600px) {
    gap: 12px;
  }
}

.chat-textarea {
  flex-grow: 1;

  ::v-deep textarea {
    font-size: 16px;

    @media (max-width: 600px) {
      font-size: 14px;
    }
  }
}

.send-btn {
  flex-shrink: 0;
}
</style>
